import { Component, Input, OnDestroy, OnInit, Type } from '@angular/core';
import { IGetClass, IModalWindow, ISetOptions } from '../../../utils/interfaces/modal-dialog.interface';
import { ModalWindowsTypes } from '../../../core/enums';
import { PdfPageService } from '../../../services/pdf-page.service';
import { Subscription } from 'rxjs';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FileConvertorService } from '../../../services/file-convertor/file-convertor-service';
import { RemoveExtensionStringPipe } from '../../../utils/pipes/remove-extension-string.pipe';
import { TabService } from '../../../services/tab.service';
import { PdfInstanceManagerService } from '../../../services/instance/pdf-instance-manager.service';
import { BlobToPdfService } from '../../../services/blob-to-pdf.service';
import { ImageToPdfConversion } from 'src/app/services/file-convertor/conversions';
import { TranslateModule } from '@ngx-translate/core';
import { Core } from '@pdftron/webviewer';
import { environment } from 'src/environments/environment';
import { ToastrDialogService } from 'src/app/services/toastr-dialog.service';
import { ToastrActionEnum, ToastrStringsEnum } from 'src/app/utils/enums';



const MAX_PAGE_OCR = 20;

@Component({
    selector: 'asg-pdf-page-select-dialog',
    templateUrl: './pdf-page-select-dialog.component.html',
    styleUrls: ['./pdf-page-select-dialog.component.scss'],
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, TranslateModule]
})

export class PdfPageSelectDialogComponent implements OnInit, OnDestroy, IModalWindow, ISetOptions {
  @Input() ok: () => void;
  @Input() no: () => void;

  private readonly subscriptions: Subscription = new Subscription();

  private loadedFile: File;
  private title: string;
  private newFile: File | null;
  private ogLastPage: number;
  private pdfDoc: Core.PDFNet.PDFDoc | null;

  pageRangeForm: FormGroup;
  pageCount: number;
  firstPage: number = 1;
  lastPage: number;
  isValidated: boolean = true;
  ocrCurrentPage: boolean = false;
  ocrCurrentDoc: boolean = false;

  constructor(
    private fb: FormBuilder,
    private pdfPageSvc: PdfPageService,
    private convertorSvc: FileConvertorService,
    private removeExtPipe: RemoveExtensionStringPipe,
    private tab: TabService,
    private pdfMgr: PdfInstanceManagerService,
    private blobToPdf: BlobToPdfService,
    private toastrService: ToastrDialogService,
  ) {
  }

  ngOnInit(): void {
    this.init();
    this.createForm();
    this.onValueChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  async ocrSelectedPages(): Promise<void> {
    if (this.firstPage && this.lastPage) {
      const ocrTitle = this.removeExtPipe.transform(this.title) + '_OCR_p' + this.firstPage + '-' + this.lastPage;
      this.newFile = await this.pdfPageSvc.extractPagesFromDocument(this.firstPage, this.lastPage, ocrTitle, this.pdfDoc);

      if (this.newFile !== null) {
        // this.convertorSvc.getImageAndCreatePdf(this.newFile);
        this.convertorSvc.makeConversion<ImageToPdfConversion>(ImageToPdfConversion, { file: this.newFile });
      }
    } else {
      console.log('Page entries are empty');
    }
  }

  getWindowType(): Type<IModalWindow> {
    return PdfPageSelectDialogComponent;
  }

  getWindowEnumType(): ModalWindowsTypes {
    return ModalWindowsTypes.PDF_PAGE_SELECT_DIALOG;
  }

  getInstanceOf(type: string): IModalWindow | ISetOptions | IGetClass | null {
    switch (type) {
      case 'IModalWindow':
        return this as unknown as IModalWindow;
      case 'ISetOptions':
        return this as unknown as ISetOptions;
      case 'IGetClass':
        return this as unknown as IGetClass;
      default: return null;
    }
  }

  getClass(): string {
    return 'pdf-page-select-dlg';
  }

  // TODO: specify proper type here
  setOptions(options: any): void {
    options = options || {};
    this.loadedFile = options.loadedFile;
    this.ocrCurrentDoc = options.currentPageMode;
  }

  private async init(): Promise<void> {
    this.title = this.loadedFile.name;
    try {
      // 27 JAN 2025
      // if a pdf was not opened yet, then PDFNet is not initilized yet and 'bloblToPdf.convertFileToPdfDoc...' will throw an error:
      // Error: PDFNet.initialize must be called and finish resolving before any other PDFNetJS function calls.
      // so check if it's ever been initialized first
      if (!this.pdfMgr.isPDFNetInitialized) {
        console.log('initialize PDFNet');
        const PDFNet = (window as any).Core.PDFNet;
        await PDFNet.initialize(environment.pdfTronLicenceKey);
      }

      // then, continue
      this.pdfDoc = await this.blobToPdf.convertFileToPdfDoc(this.loadedFile);
      const res = await this.pdfDoc?.initSecurityHandler() ?? true;
      // if false, then pdf is password protected
      if (!res) {
        // close the dialog
        this.no();
        // show toaster 
        this.toastrService.showToastrByType(ToastrActionEnum.Error, ToastrStringsEnum.OCRFailedPasswordNeeded, false);
        return
      }

      this.pageCount = this.pdfDoc ? await this.pdfDoc.getPageCount() : 0;
      this.lastPage = this.pageCount > MAX_PAGE_OCR ? MAX_PAGE_OCR : this.pageCount;
      this.ogLastPage = this.lastPage;
    } catch (err) {
        console.error(err);
        // close the dialog
        this.no();
        // show toaster 
        this.toastrService.showToastrByType(ToastrActionEnum.Error, ToastrStringsEnum.OCRFailedPDFTronError, false);
    }
  }

  private createForm(): void {
    this.pageRangeForm = this.fb.group({
      pageStartControl: [this.firstPage],
      pageEndControl: [this.lastPage],
      ocrPageControl: [this.ocrCurrentPage]
    });
  }

  private onValueChanges(): void {
    const pageStartControl = this.pageRangeForm.get('pageStartControl');

    if (pageStartControl) {
      this.subscriptions.add(
        pageStartControl.valueChanges.subscribe((value: number) => {
          this.pageNumberChange('first', Number(value));
        })
      );
    }

    const pageEndControl = this.pageRangeForm.get('pageEndControl');

    if (pageEndControl) {
      this.subscriptions.add(
        pageEndControl.valueChanges.subscribe((value: number) => {
          this.pageNumberChange('last', Number(value));
        })
      );
    }

    const ocrPageControl = this.pageRangeForm.get('ocrPageControl');

    if (ocrPageControl) {
      this.subscriptions.add(
        ocrPageControl.valueChanges.subscribe(async (bool) => {
          if (this.ocrCurrentPage === bool) {
            this.isValidated = this.validateRange();
            return;
          }
          this.ocrCurrentPage = bool;
          if (!this.ocrCurrentPage) {
            this.firstPage = 1;
            this.lastPage = this.ogLastPage;
          } else if (this.tab.activeTab) {
            const viewer = await this.pdfMgr.getDocViewer(this.tab.activeTab.id);

            if (viewer) {
              this.firstPage = this.lastPage = await viewer.getCurrentPage();
            }
          }
        })
      );
    }
  }

  private pageNumberChange(page: string, pageNr: number): void {
    page === 'first' ? this.firstPage = pageNr : this.lastPage = pageNr;
    this.isValidated = this.validateRange();
    this.ocrCurrentPage = false; // checkbox doesn't react to dynamic change

    const ocrPageControl = this.pageRangeForm.get('ocrPageControl');

    if (ocrPageControl) {
      ocrPageControl.setValue(this.ocrCurrentPage); // fix lack of dynamic change
    }
  }

  private validateRange(): boolean {
    return !(
      this.lastPage === null || this.firstPage === null || // not empty
      isNaN(this.lastPage) || isNaN(this.firstPage) || // is number
      this.lastPage < 1 || this.firstPage < 1 || // no negatives
      this.lastPage - this.firstPage >= MAX_PAGE_OCR || // no more than 20 pages
      this.lastPage > this.pageCount || // not higher than maximum
      (this.lastPage > this.pageCount && this.pageCount < MAX_PAGE_OCR) || // no more than document max if < 20
      this.lastPage < this.firstPage || // last not bigger than first
      this.lastPage.toString() === '' || this.firstPage.toString() === '' // actually has input
    );
  }
}
